// src/features/merchantConfig/lib/configKeyCatalog.js
//
// Single source of truth for the 12 merchant configuration keys.
//
// Each entry maps the wire-format `merchant_configuration_key` enum to:
//   - displayName   : human-friendly category name shown in sidebar / header
//   - slug          : URL-safe path segment used for deep-linking (`/settings/configuration/<slug>`)
//   - valueField    : the snake_case value-block field name returned inside the envelope
//                     (NOT mechanically derivable — see API doc §2 for the quirks)
//   - description   : short subtitle shown in the detail panel header
//   - icon          : optional lucide-react icon name (string) for the sidebar
//
// IMPORTANT: `valueField` is canonical. The renderer uses it to pluck the inner
// value block out of the GET envelope. Do not change without verifying against a
// fresh GET response.

import {
  Settings,
  Tag,
  Receipt,
  FileSpreadsheet,
  Mail,
  IndianRupee,
  StickyNote,
  MessageSquare,
  Bell,
  ClipboardCheck,
  PhoneCall,
  Webhook,
} from "lucide-react";

/**
 * @typedef {Object} MerchantConfigCatalogEntry
 * @property {string} key            - The wire-format enum value, case-sensitive.
 * @property {string} displayName    - Humanized category title.
 * @property {string} slug           - URL-safe path segment.
 * @property {string} valueField     - JSON field name of the value block inside the envelope.
 * @property {string} description    - Short subtitle for the detail-panel header.
 * @property {React.ComponentType} icon
 */

/** @type {MerchantConfigCatalogEntry[]} */
export const MERCHANT_CONFIG_CATALOG = [
  {
    key: "MERCHANT_GENERAL_CONFIGURATIONS",
    displayName: "General",
    slug: "general",
    valueField: "merchant_general_configurations_value",
    description: "Core account-wide defaults such as pickup and return addresses.",
    icon: Settings,
  },
  {
    key: "MERCHANT_SHIPMENT_LABEL_CONFIGURATIONS",
    displayName: "Shipment Label",
    slug: "shipment-label",
    valueField: "merchant_shipment_label_configurations_value",
    description: "Rules and template settings applied when generating shipping labels.",
    icon: Tag,
  },
  {
    key: "MERCHANT_SHIPMENT_INVOICE_CONFIGURATIONS",
    displayName: "Shipment Invoice",
    slug: "shipment-invoice",
    valueField: "merchant_shipment_invoice_configurations_value",
    description: "Invoice template and content settings on shipments.",
    icon: Receipt,
  },
  {
    key: "MERCHANT_ORDER_SUMMARY_REPORT_CONFIGURATIONS",
    displayName: "Order Summary Report",
    slug: "order-summary-report",
    // Singular `configuration_value` (per API doc §2)
    valueField: "merchant_order_summary_report_configuration_value",
    description: "Daily / periodic order-summary report content and recipients.",
    icon: FileSpreadsheet,
  },
  {
    key: "MERCHANT_SCHEDULED_EMAIL_REPORT_CONFIGURATIONS",
    displayName: "Scheduled Email Reports",
    slug: "scheduled-email-reports",
    // Singular `configuration_value` (per API doc §2)
    valueField: "merchant_scheduled_email_report_configuration_value",
    description: "Reports that are emailed to your operations team on a schedule.",
    icon: Mail,
  },
  {
    key: "MERCHANT_COD_REMITTANCE_REPORT_CONFIGURATIONS",
    displayName: "COD Remittance",
    slug: "cod-remittance",
    valueField: "merchant_cod_remittance_configurations_value",
    description: "Frequency and recipients for Cash-on-Delivery remittance reports.",
    icon: IndianRupee,
  },
  {
    key: "MERCHANT_EXTERNAL_SHIPMENT_NOTES_CONFIGURATIONS",
    displayName: "External Shipment Notes",
    slug: "external-shipment-notes",
    valueField: "merchant_external_shipment_notes_configurations_value",
    description: "Notes printed on shipments for the receiver / courier partner.",
    icon: StickyNote,
  },
  {
    key: "MERCHANT_SMS_NOTIFICATION_CONFIGURATIONS",
    displayName: "SMS Notifications",
    slug: "sms-notifications",
    valueField: "merchant_sms_notification_configurations_value",
    description: "SMS templates and trigger events for buyer notifications.",
    icon: MessageSquare,
  },
  {
    key: "MERCHANT_WHATSAPP_NOTIFICATION_CONFIGURATIONS",
    displayName: "WhatsApp Notifications",
    slug: "whatsapp-notifications",
    // Note the `whats_app` split (Jackson splits at every uppercase, per API doc §2)
    valueField: "merchant_whats_app_notification_configurations_value",
    description: "WhatsApp templates and trigger events for buyer notifications.",
    icon: Bell,
  },
  {
    key: "MERCHANT_CREATE_ORDER_CONFIRMATION_CONFIGURATIONS",
    displayName: "Order Confirmation",
    slug: "order-confirmation",
    valueField: "merchant_create_order_confirmation_configurations_value",
    description: "Behaviour applied when confirming new orders (auto-confirm, IVR, etc.).",
    icon: ClipboardCheck,
  },
  {
    key: "MERCHANT_NDR_CUSTOMER_INTERACTION_CONFIGURATIONS",
    displayName: "NDR Customer Interaction",
    slug: "ndr-customer-interaction",
    valueField: "merchant_ndr_customer_interaction_configurations_value",
    description: "How buyers are contacted on non-delivery (NDR) events.",
    icon: PhoneCall,
  },
  {
    key: "MERCHANT_WEBHOOK_CONFIGURATIONS",
    displayName: "Webhooks",
    slug: "webhooks",
    valueField: "merchant_webhook_configurations_value",
    description: "Outbound webhook subscriptions and target URLs.",
    icon: Webhook,
  },
];

/** O(1) lookups by key. */
export const CATALOG_BY_KEY = Object.fromEntries(
  MERCHANT_CONFIG_CATALOG.map((entry) => [entry.key, entry])
);

/** O(1) lookups by slug. */
export const CATALOG_BY_SLUG = Object.fromEntries(
  MERCHANT_CONFIG_CATALOG.map((entry) => [entry.slug, entry])
);

/**
 * Find a catalog entry by either its enum key or its URL slug.
 * @param {string} keyOrSlug
 * @returns {MerchantConfigCatalogEntry | undefined}
 */
export function findCatalogEntry(keyOrSlug) {
  if (!keyOrSlug) return undefined;
  return CATALOG_BY_KEY[keyOrSlug] || CATALOG_BY_SLUG[keyOrSlug];
}
